import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ASSOC } from "../lib/api";
import ChatPanel from "../components/ChatPanel";
import { Toaster } from "../components/ui/sonner";
import { Loader2, ShieldAlert, LogOut } from "lucide-react";
import { Button } from "../components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function EspaceMembre() {
  const { token } = useParams();
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) { setError("Lien invalide."); setLoading(false); return; }
    axios.get(`${API}/member-portal/${token}`)
      .then((r) => setMember(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Lien d'accès invalide ou expiré."))
      .finally(() => setLoading(false));
  }, [token]);

  const memberApiClient = async (method, path, body) => {
    const res = await axios.request({
      method,
      url: `${API}${path}`,
      data: body,
      headers: { Authorization: `Member ${token}` },
    });
    return res.data;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-bg flex items-center justify-center">
        <Loader2 className="animate-spin text-brand-navy" size={28} />
      </div>
    );
  }

  if (error || !member) {
    return (
      <div className="min-h-screen bg-brand-bg flex items-center justify-center p-6">
        <div className="bg-white border border-brand-border rounded-xl shadow-sm p-10 max-w-md w-full text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-red-50 flex items-center justify-center mb-4">
            <ShieldAlert className="text-red-500" size={32} />
          </div>
          <h1 className="font-serif text-2xl text-brand-navy mb-2">Accès impossible</h1>
          <p className="text-gray-600 text-sm">{error || "Lien invalide."}</p>
          <p className="text-xs text-gray-400 mt-6">
            Si vous êtes adhérent(e), vérifiez l'email reçu lors de la validation de votre adhésion
            — il contient votre lien personnel.
          </p>
          <Button asChild className="mt-6 bg-brand-navy hover:bg-brand-navy/90 text-white">
            <a href={`https://${ASSOC.website}`} target="_blank" rel="noreferrer">
              Site de l'association
            </a>
          </Button>
        </div>
      </div>
    );
  }

  const fullName = `${member.first_name} ${member.last_name}`.trim();

  return (
    <div className="min-h-screen bg-brand-bg" data-testid="espace-membre">
      <header className="bg-brand-navy text-white">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center gap-4">
          <img src={ASSOC.logo} alt="Logo" className="w-12 h-12 bg-white rounded-full p-1 object-contain" />
          <div className="flex-1">
            <div className="small-caps text-white/60 text-[11px]">Espace membre</div>
            <h1 className="font-serif text-2xl leading-tight">Bonjour {fullName}</h1>
            <div className="text-xs text-white/70 mt-0.5">Carte n° {member.member_number}</div>
          </div>
          <Link to="/" className="text-white/70 hover:text-white text-xs flex items-center gap-1">
            <LogOut size={14} /> Quitter
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        <div className="mb-5">
          <h2 className="font-serif text-3xl text-brand-navy mb-1">Forum des membres</h2>
          <p className="text-sm text-gray-500">
            Échangez en temps réel avec les autres adhérents et le bureau sur les projets de l'association.
          </p>
        </div>

        <ChatPanel
          channel="members"
          apiClient={memberApiClient}
          canDelete={false}
          currentLabel={fullName}
          placeholder="Écrivez un message à la communauté…"
        />

        <footer className="mt-8 text-center text-xs text-gray-400 leading-relaxed">
          {ASSOC.name} — Association loi 1901<br />
          {ASSOC.address}<br />
          Conservez ce lien personnel — il vous donne accès au forum réservé aux membres.
        </footer>
      </main>

      <Toaster position="top-right" richColors />
    </div>
  );
}
